import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Particles } from '@tsparticles/react';
import { loadFull } from 'tsparticles';
import { 
  Calculator, Users, Award, DollarSign, FileText, Building, User, Phone, 
  Mail, MapPin, Menu, X, CheckCircle, Star, ArrowRight, Shield, Rocket, 
  TrendingUp, Clock, Globe, BookOpen, Gavel, BarChart2
} from 'lucide-react';
import bgAccounting from '../assets/accounting-bg.jpg';

// Particle Background Component
const ParticleBackground = ({ showImage = false }) => {
  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  const particlesOptions = {
    particles: {
      number: { value: 50, density: { enable: true, value_area: 800 } },
      color: { value: ["#FFD700", "#F59E0B", "#FFFFFF"] },
      shape: { type: "circle" },
      opacity: { value: 0.5, random: true },
      size: { value: 3, random: true },
      move: { enable: true, speed: 0.5, out_mode: "out" },
      links: { enable: true, distance: 150, color: "#FFFFFF", opacity: 0.2, width: 1 }
    },
    interactivity: {
      events: {
        onhover: { enable: true, mode: "grab" },
        onclick: { enable: true, mode: "push" }
      },
      modes: {
        grab: { distance: 200, links: { opacity: 0.5 } },
        push: { particles_nb: 4 }
      }
    },
    retina_detect: true
  };

  return (
    <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
      {showImage && (
        <img
          src={bgAccounting}
          alt="Accounting Background"
          className="w-full h-full object-cover opacity-20"
        />
      )}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={particlesOptions}
        className="absolute inset-0 w-full h-full"
      />
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);
  
  return (
    <motion.span
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 100 }}
    >
      {count}{suffix}
    </motion.span>
  );
};

// Navigation Component
const Navigation = ({ activeSection, setActiveSection, mobileMenuOpen, setMobileMenuOpen }) => {
  const navItems = [
    { id: 'home', label: 'Home', icon: User },
    { id: 'about', label: 'About', icon: Shield },
    { id: 'services', label: 'Services', icon: Building },
    { id: 'testimonials', label: 'Testimonials', icon: Star },
    { id: 'contact', label: 'Contact', icon: Phone }
  ];

  const handleNavClick = (sectionId) => {
    console.log(`Navigating to section: ${sectionId}`);
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      if (element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      setTimeout(() => setMobileMenuOpen(false), 300);
    } else {
      console.error(`Section ${sectionId} not found`);
    }
  };

  return (
    <motion.nav 
      className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-lg z-[1000]"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 120 }}
    >
      <div className="max-w-full mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          <motion.div className="flex-shrink-0" whileHover={{ scale: 1.05 }}>
          <button
  onClick={() => handleNavClick('home')}
  className="text-3xl font-extrabold bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 bg-clip-text text-transparent hover:brightness-125 transition-all duration-300"
  style={{
    textShadow: '0 1px 1px rgba(250, 186, 60, 0.4), 0 2px 2px rgba(255, 136, 0, 0.3)',
  }}
  aria-label="Go to Home"
>
  Nandu eTax
</button>


          </motion.div>

          <div className="hidden md:block">
            <div className="flex space-x-8">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  onTouchStart={() => handleNavClick(item.id)}
                  className={`px-3 py-2 text-sm font-medium transition-all duration-300 touch-manipulation ${
                    activeSection === item.id
                      ? 'text-yellow-500 border-b-2 border-yellow-500'
                      : 'text-gray-800 hover:text-yellow-500'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={`Navigate to ${item.label}`}
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
          </div>

          <div className="md:hidden">
            <motion.button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              onTouchStart={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-800 hover:text-yellow-500 p-2 touch-manipulation"
              whileTap={{ scale: 0.9 }}
              aria-controls="mobile-menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              className="md:hidden bg-white border-t overflow-x-hidden z-[1001]"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      onTouchStart={() => handleNavClick(item.id)}
                      className={`w-full text-left px-3 py-2 flex items-center space-x-2 text-base font-medium transition-all duration-300 touch-manipulation pointer-events-auto ${
                        activeSection === item.id
                          ? 'text-yellow-500 bg-yellow-50'
                          : 'text-gray-800 hover:text-yellow-500 hover:bg-yellow-50'
                      }`}
                      whileHover={{ x: 10 }}
                      aria-label={`Navigate to ${item.label}`}
                      data-testid={`nav-${item.id}`}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Home Section (Updated)
const HomeSection = () => {
  const handleButtonClick = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = document.querySelector('nav')?.offsetHeight || 64; // Fallback to 64px
      const isMobile = window.innerWidth < 768;
      const mobileOffset = isMobile ? 10 : 0; // Small offset for mobile
      const y = element.getBoundingClientRect().top + window.scrollY - headerHeight - mobileOffset;

      setTimeout(() => {
        window.scrollTo({ top: y, behavior: 'smooth' });
        console.log('Scrolling to:', { sectionId, y, headerHeight, isMobile }); // Debug
      }, 100);
    } else {
      console.error(`Section ${sectionId} not found`);
    }
  };

  return (
    <section id="home" className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white overflow-hidden relative">
      <ParticleBackground showImage={true} />
      
      <motion.div 
        className="relative z-10 max-w-full mx-auto px-4 sm:px-6 pt-20 pb-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen">
          <motion.div 
            className="space-y-8"
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-center sm:text-left">
              <div className="inline-flex flex-col items-center sm:items-start">
                <span className="text-black">Welcome to</span>
                <div className="flex gap-2 sm:gap-4 flex-wrap justify-center sm:justify-start">
                  <motion.span
                    className="bg-gradient-to-r from-yellow-500 to-orange-400 bg-clip-text text-transparent"
                    whileHover={{ scale: 1.05 }}
                  >
                    Nandu
                  </motion.span>
                  <motion.span
                    className="bg-gradient-to-r from-yellow-500 to-orange-400 bg-clip-text text-transparent"
                    whileHover={{ scale: 1.05 }}
                  >
                    eTax
                  </motion.span>
                </div>
              </div>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-800 leading-relaxed">
              Your trusted partner for comprehensive tax and business compliance services. 
              We simplify complex regulations and maximize your savings with expert guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button 
                onClick={() => handleButtonClick('about')}
                className="bg-gradient-to-r from-orange-400 to-amber-400 hover:from-orange-500 hover:to-amber-500 px-8 py-4 rounded-full font-semibold text-lg shadow-xl flex items-center justify-center space-x-2 text-gray-800"
                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(255,255,255,0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                <Rocket size={20} />
                <span>Get Started</span>
              </motion.button>
              <motion.button 
                onClick={() => handleButtonClick('services')}
                className="border-2 border-gray-900 bg-transparent hover:bg-gray-900 hover:text-yellow-500 px-8 py-4 rounded-full font-semibold text-lg text-gray-900"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>

          <motion.div 
            className="flex justify-center"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              <motion.div 
                className="w-80 h-80 lg:w-96 lg:h-96 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-2xl"
                animate={{ 
                  scale: [1, 1.05, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Calculator size={120} className="text-gray-900" />
              </motion.div>
              <motion.div 
                className="absolute -top-4 -right-4 w-24 h-24 bg-amber-400 rounded-full flex items-center justify-center shadow-lg"
                animate={{ y: [-10, 10] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <TrendingUp size={32} className="text-gray-900" />
              </motion.div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
          {[
            { icon: Users, label: 'Happy Clients', value: 500, suffix: '+' },
            { icon: FileText, label: 'Registrations & Filings', value: 1200, suffix: '+' },
            { icon: Award, label: 'Years Experience', value: 15, suffix: '+' },
            { icon: DollarSign, label: 'Money Saved', value: 50, suffix: 'L+' }
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div 
                key={index}
                className="text-center bg-white/10 backdrop-blur-md rounded-2xl p-6 hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(255,255,255,0.2)" }}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Icon size={48} className="mx-auto mb-4 text-yellow-500" />
                <div className="text-3xl font-bold mb-2 text-gray-900">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-gray-800">{stat.label}</div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-gradient-to-br from-white to-gray-100 relative overflow-x-hidden">
      <ParticleBackground />
      <div className="max-w-full mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent mb-6">
            About Nandu eTax
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your trusted partner in navigating the complex world of taxation and compliance
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            className="space-y-8"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-3xl p-12 flex items-center justify-center shadow-2xl"
              whileHover={{ scale: 1.05, rotate: 2 }}
            >
              <Shield size={120} className="text-gray-800" />
            </motion.div>
          </motion.div>

          <motion.div 
            className="space-y-8"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-3xl font-bold text-gray-800">
              Your Trusted Compliance Partner
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              With over 10 years of experience, Nandu eTax specializes in tax consultation, 
              business registrations, compliance, and financial services for individuals and businesses.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our certified professionals stay updated with the latest regulations to deliver 
              accurate, reliable, and efficient solutions tailored to your needs.
            </p>

            <div className="space-y-4">
              {[
                'Expert Tax & Compliance Services',
                'Business Registration & Licensing',
                '24/7 Customer Support',
                'Secure & Confidential'
              ].map((feature, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-center space-x-3"
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <CheckCircle className="text-yellow-500 flex-shrink-0" size={24} />
                  <span className="text-lg text-gray-700">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);

const services = [
  {
    title: 'Business & Entity Registrations',
    description: 'Complete setup and registration support for all business types, ensuring smooth launch and regulatory compliance.',
    highlights: ['Company Registration', 'LLP Registration', 'Start-up India Registration'],
    details: [
      { name: 'Company Registration', type: 'Public / Private Limited, OPC' },
      { name: 'LLP Registration', type: '' },
      { name: 'Firm Registration', type: '' },
      { name: 'Society Registration', type: '' },
      { name: 'Trust Registration', type: '' },
      { name: 'Start-up India Registration', type: '' },
      { name: 'DARPAN ID', type: '' },
      { name: 'MSME (Udyam) Registration', type: '' }
    ],
    icon: Building,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Financial Planning & Analysis',
    description: 'Strategic financial services to guide startups and businesses toward confident growth, control, and long-term value.',
    highlights: [
      'Financial planning & analysis',
      'Budgeting & forecasting',
      'Fundraising support and investor presentations'
    ],
    details: [
      { name: 'Financial planning & analysis', type: '' },
      { name: 'Budgeting & forecasting', type: '' },
      { name: 'Fundraising support and investor presentations', type: '' },
      { name: 'Project finance and loan syndication', type: '' },
      { name: 'Working capital optimization', type: '' },
      { name: 'Internal control systems and MIS reporting', type: '' },
      { name: 'Strategic Decision Making', type: '' },
      { name: 'Long-term Value Creation', type: '' },
      { name: 'Transparency & Accuracy', type: '' },
      { name: 'Expert Financial Guidance', type: '' },
      { name: 'Risk Mitigation', type: '' },
      { name: 'Growth Optimization', type: '' }
    ],
    icon: BarChart2,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Licenses & Government Registrations',
    description: 'End-to-end assistance for obtaining government licenses and mandatory statutory registrations.',
    highlights: ['Food License (FSSAI)', 'Trade License', 'RERA Registration'],
    details: [
      { name: 'Labour License', type: '' },
      { name: 'Trade License', type: '' },
      { name: 'Food License (FSSAI)', type: '' },
      { name: 'RERA Registration', type: '' },
      { name: 'PASARA Registration', type: 'Private Security Agencies' },
      { name: 'APEDA Registration', type: '' },
      { name: 'DMHO License', type: '' },
      { name: 'Fire Department NOC', type: '' },
      { name: 'Pollution Control Board NOC', type: '' },
      { name: 'Bio-Medical Waste Authorization', type: '' },
      { name: 'Factory License', type: '' },
      { name: 'PF, ESIC Registration', type: '' },
      { name: 'Professional Tax (PT) Registration', type: '' },
      { name: 'Digital Signature Certificate (DSC)', type: '' },
      { name: 'Trade Mark Registration', type: '' }
    ],
    icon: FileText,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Import & Export Compliance',
    description: 'Specialized assistance for import/export codes and certifications required for global trade operations.',
    highlights: ['Import Export Code (IEC)', 'RCMC', 'LUT under GST'],
    details: [
      { name: 'Import Export Code (IEC)', type: '' },
      { name: 'AD Code', type: 'Bank Issued' },
      { name: 'RCMC', type: 'Registration-Cum-Membership Certificate' },
      { name: 'LUT under GST', type: '' },
      { name: 'BIS Hallmark License', type: '' },
      { name: 'DSCO Registration', type: 'Pharma' },
      { name: 'APEDA License', type: 'Agri products' },
      { name: 'WPC / TEC Clearance', type: 'Electronics' },
      { name: 'GJEPC Membership', type: 'Gems & Jewellery' },
      { name: 'FIEO Membership', type: '' },
      { name: 'EPCG Scheme', type: '' },
      { name: 'AEPC Registration', type: 'Apparel' },
      { name: 'RCMC from EEPC', type: 'Engineering goods' },
      { name: 'ARAI / BIS Approval', type: 'Automobile' }
    ],
    icon: Globe,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Direct Tax & Financial Filings',
    description: 'Comprehensive income tax filing and compliance for individuals, firms, companies, and institutions.',
    highlights: ['Income Tax Returns (ITR)', 'GST Returns', 'TDS Returns'],
    details: [
      { name: 'Income Tax Returns (ITR)', type: 'Salaried, Freelancers, Business Owners, Pensioners, NRIs, HUFs, Firms, Trusts, LLPs, Companies, AOPs/BOIs' },
      { name: 'TDS Returns', type: '' },
      { name: 'Monthly Filings', type: 'PF, ESIC, PT, TDS, Payroll' }
    ],
    icon: Calculator,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Indirect Tax Services',
    description: 'End-to-end GST advisory, filing, and litigation support tailored to your business structure.',
    highlights: [
      'GST Registration & Filing',
      'Reply to GST Notices',
      'Representation before Authorities'
    ],
    details: [
      { name: 'GST registration, filing, and return reconciliation', type: '' },
      { name: 'Reply to GST notices (DRC-01A, ASMT, SCN, etc.)', type: '' },
      { name: 'Representation before GST officers and appellate authorities', type: '' },
      { name: 'E-way bill and input tax credit (ITC) management', type: '' },
      { name: 'Advisory on supply chain, input classification, and RCM compliance', type: '' },
      { name: 'Assistance in audits, departmental queries, and refund claims', type: '' },
      { name: 'Timely Compliance', type: 'Benefit' },
      { name: 'Strategic Tax Insight', type: 'Benefit' },
      { name: 'Risk Minimization', type: 'Benefit' },
      { name: 'Expert Representation', type: 'Benefit' },
      { name: 'Precision Handling', type: 'Benefit' },
      { name: 'Comprehensive Support', type: 'Benefit' }
    ],
    icon: FileText,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'ROC Filings & Company Compliance',
    description: 'Annual and event-based ROC filings and statutory compliance for private and public companies.',
    highlights: ['Annual Compliances', 'Event-Based Filings', 'Statutory Registers'],
    details: [
      { name: 'Annual Compliances', type: 'ADT-1, AOC-4, MGT-7, DIR-3 KYC, AGM, Board Meetings' },
      { name: 'Event-Based Filings', type: 'DIR-12, SH-7, PAS-3, INC-22, CHG-1/CHG-4, ADT-3, DPT-3, MSME Form I, BEN-2' },
      { name: 'Other Mandatory Compliances', type: 'MBP-1, DIR-8, MGT-14' },
      { name: 'Statutory Register Maintenance', type: '' }
    ],
    icon: Award,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Bookkeeping, Accounting & Audit',
    description: 'Streamlined accounting, bookkeeping, and auditing solutions for all business entities and sectors.',
    highlights: ['Bookkeeping', 'Statutory Audit', 'Internal Audit'],
    details: [
      { name: 'Bookkeeping', type: 'Individuals, Firms, Companies, Trusts, HUFs, AOPs' },
      { name: 'Statutory Audit', type: 'Individuals, Firms, Companies, Trusts, HUFs, AOPs' },
      { name: 'Internal Audit', type: 'Individuals, Firms, Companies, Trusts, HUFs, AOPs' }
    ],
    icon: BookOpen,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Appeals & Revisions',
    description: 'Expert assistance in managing and representing tax-related appeals before tax authorities.',
    highlights: ['Direct Tax Appeals', 'Indirect Tax Appeals'],
    details: [
      { name: 'Direct Tax Appeals', type: 'Individuals, Firms, Companies, Trusts, LLPs, HUFs, AOPs/BOIs, Local Authorities' },
      { name: 'Indirect Tax Appeals', type: 'Individuals, Firms, Companies, Trusts, LLPs, HUFs, AOPs/BOIs, Local Authorities' }
    ],
    icon: Gavel,
    gradient: 'from-yellow-400 to-amber-500'
  },
  {
    title: 'Loan Assistance & Project Reports',
    description: 'Complete loan guidance and project documentation for funding from banks and NBFCs.',
    highlights: ['Bank Loans', 'NBFC Loans'],
    details: [
      { name: 'Bank Loans', type: 'Secured: Home, LAP, Vehicle, Gold, FD, Agricultural; Unsecured: Personal, Education, Credit Card, Consumer Durable; Business: Working Capital, Term, OD, CC, Bill Discounting, Project Finance, Export/Import' },
      { name: 'NBFC Loans', type: 'Retail: Personal, Gold, Consumer Durable, Vehicle, Two-Wheeler, Home, Education; Business/SME: MSME, Working Capital, Equipment, Unsecured, Invoice Financing; Specialized: Microfinance, Securities, Construction Equipment, Tractor, LAP' }
    ],
    icon: DollarSign,
    gradient: 'from-yellow-400 to-amber-500'
  }
];


  const closeModal = () => setSelectedService(null);

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-white to-gray-100 relative overflow-x-hidden">
      <ParticleBackground showImage={false} />
      <div className="relative z-10 max-w-full mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent mb-6">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive solutions for tax, compliance, and financial needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                className="bg-white rounded-3xl shadow-xl overflow-hidden"
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                  transition: { duration: 0.3 }
                }}
              >
                <div className={`bg-gradient-to-r ${service.gradient} p-8 text-gray-800 text-center`} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <Icon size={64} className="mx-auto mb-4" />
                  </motion.div>
                  <h3 className="text-2xl font-bold">{service.title}</h3>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Services:</h4>
                    {service.highlights.map((highlight, idx) => (
                      <motion.div 
                        key={idx} 
                        className="flex items-center space-x-3 text-gray-700"
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.4, delay: idx * 0.1 }}
                      >
                        <CheckCircle className="text-yellow-500 flex-shrink-0" size={20} />
                        <span>{highlight}</span>
                      </motion.div>
                    ))}
                    <motion.button
                      onClick={() => setSelectedService(service)}
                      className="mt-4 w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 px-6 py-2 rounded-xl text-gray-800 font-semibold"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Read More
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <AnimatePresence>
          {selectedService && (
            <motion.div
              className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            >
              <motion.div
                className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-800">{selectedService.title}</h3>
                  <motion.button
                    onClick={closeModal}
                    className="text-gray-600 hover:text-gray-800"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={24} />
                  </motion.button>
                </div>
                <p className="text-gray-600 mb-6">{selectedService.description}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 border-b text-gray-700 font-semibold">Service</th>
                        <th className="p-3 border-b text-gray-700 font-semibold">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedService.details.map((item, idx) => (
                        <motion.tr
                          key={idx}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.1 }}
                          className="hover:bg-gray-50"
                        >
                          <td className="p-3 border-b text-gray-800">{item.name}</td>
                          <td className="p-3 border-b text-gray-600">{item.type || 'N/A'}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <motion.button
                  onClick={closeModal}
                  className="mt-6 w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 px-6 py-2 rounded-xl text-gray-800 font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Close
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Business Owner',
      content: 'Nandu eTax streamlined my company registration and GST filings. Their expertise is unmatched!',
      rating: 5,
      avatar: 'R'
    },
    {
      name: 'Priya Sharma',
      role: 'Freelancer',
      content: 'Professional tax filing and compliance services with quick turnaround. Highly recommended!',
      rating: 5,
      avatar: 'P'
    },
    {
      name: 'Amit Patel',
      role: 'Exporter',
      content: 'Their import-export compliance and loan assistance helped my business expand seamlessly.',
      rating: 5,
      avatar: 'A'
    }
  ];

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-white to-gray-100 relative overflow-x-hidden">
      <ParticleBackground />
      <div className="max-w-full mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent mb-6">
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Trusted by hundreds of satisfied clients across India
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-3xl p-8 shadow-xl"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              whileHover={{ 
                scale: 1.03, 
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                transition: { duration: 0.3 }
              }}
            >
              <div className="text-center mb-6">
                <motion.div 
                  className="w-20 h-20 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-gray-800 text-2xl font-bold mx-auto mb-4"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  {testimonial.avatar}
                </motion.div>
                <h4 className="text-xl font-bold text-gray-800">{testimonial.name}</h4>
                <p className="text-gray-600">{testimonial.role}</p>
              </div>
              
              <div className="flex justify-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                  >
                    <Star size={20} className="text-yellow-400 fill-current" />
                  </motion.div>
                ))}
              </div>
              
              <p className="text-gray-700 italic text-center leading-relaxed">
                "{testimonial.content}"
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white relative overflow-x-hidden">
      <ParticleBackground />
      <div className="max-w-full mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Contact Us
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to simplify your taxes, registrations, and compliance? Get in touch with our expert team
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div 
            className="bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl"
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-bold mb-8">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {['name', 'email', 'phone', 'message'].map((field, index) => (
                <motion.div
                  key={field}
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <label className="block text-sm font-medium mb-2">
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </label>
                  {field === 'message' ? (
                    <textarea
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-white placeholder-gray-300 resize-none"
                      placeholder="How can we help you?"
                    />
                  ) : (
                    <input
                      type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 text-white placeholder-gray-300"
                      placeholder={field === 'email' ? 'your.email@example.com' : 
                                  field === 'phone' ? '+91 XXXXX XXXXX' : 
                                  'Your Name'}
                    />
                  )}
                </motion.div>
              ))}
              
              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 px-8 py-4 rounded-xl font-semibold text-lg shadow-xl flex items-center justify-center space-x-2 text-gray-800"
                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0,0,0,0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Send Message</span>
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight size={20} />
                </motion.div>
              </motion.button>
            </form>
          </motion.div>

          <motion.div 
            className="space-y-8"
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {[
              {
                icon: Phone,
                title: 'Phone',
                content: '+91 91602 67979',
                gradient: 'from-yellow-400 to-amber-500'
              },
              {
                icon: Mail,
                title: 'Email',
                content: 'info@nanduetax.com',
                gradient: 'from-yellow-400 to-amber-500'
              },
              {
                icon: MapPin,
                title: 'Address',
                content: '3rd floor, Sri Rasthu Towers, 74/A, Vengal Rao Nagar, Madhura Nagar, Hyderabad, Telangana 500038',
                gradient: 'from-yellow-400 to-amber-500'
              },
              {
                icon: Clock,
                title: 'Working Hours',
                content: 'Mon - Sat: 9 AM - 7 PM',
                gradient: 'from-yellow-400 to-amber-500'
              }
            ].map((contact, index) => {
              const Icon = contact.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-xl flex items-center space-x-6"
                  whileHover={{ scale: 1.03 }}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <motion.div 
                    className={`w-16 h-16 bg-gradient-to-r ${contact.gradient} rounded-2xl flex items-center justify-center flex-shrink-0`}
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <Icon size={28} className="text-gray-800" />
                  </motion.div>
                  <div>
                    <h4 className="text-xl font-bold mb-2">{contact.title}</h4>
                    <p className="text-gray-300 text-lg">{contact.content}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-800 text-white py-12 relative overflow-x-hidden">
      <ParticleBackground />
      <div className="max-w-full mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent mb-4">
            Nandu eTax
          </h3>
          <p className="text-gray-400 mb-8">
            Your trusted partner for all tax, registration, and compliance needs
          </p>
          <div className="flex justify-center space-x-8 mb-8">
            <motion.div 
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
            >
              <Phone size={20} className="text-yellow-400" />
              <span>+91 91602 67979</span>
            </motion.div>
            <motion.div 
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
            >
              <Mail size={20} className="text-yellow-400" />
              <span>info@nanduetax.com</span>
            </motion.div>
          </div>
          <div className="border-t border-gray-700 pt-8">
            <p className="text-gray-500">
              © 2024 Nandu eTax. All rights reserved. | Simplifying taxes, maximizing savings.
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll spy functionality
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add global styles for overflow, box-sizing, and smooth scroll
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body {
        font-family: 'Inter', sans-serif;
        overflow-x: hidden;
        scroll-behavior: smooth;
      }
      * {
        box-sizing: border-box;
      }
      [data-testid^="nav-"] {
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
    `;
    document.head.appendChild(style);
    
    return () => document.head.removeChild(style);
  }, []);

  // Debug section availability
  useEffect(() => {
    const sections = ['home', 'about', 'services', 'testimonials', 'contact'];
    sections.forEach(section => {
      if (!document.getElementById(section)) {
        console.warn(`Section ${section} not found on mount`);
      }
    });
  }, []);

  return (
    <div className="w-full overflow-x-hidden">
      <Navigation 
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <main>
        <HomeSection />
        <AboutSection />
        <ServicesSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      
      <Footer />

      <motion.div 
        className="fixed bottom-8 right-4 z-40"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
      >
        <motion.button 
          className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-gray-800 p-4 rounded-full shadow-2xl"
          whileHover={{ scale: 1.1, rotate: 360 }}
          transition={{ duration: 0.3 }}
        >
          <Phone size={24} />
        </motion.button>
      </motion.div>
    </div>
  );
};

export default Home;